import { useEffect, useRef, useState } from "react";
import * as api from "../api/client";
import { primarySpeaker } from "../logic/npcSpeaker";
import { useAdminMode } from "../logic/useAdminMode";
import { E32 } from "../theme/endesga32";
import { pixelTheme } from "../theme/pixel";
import { simplePanelStyle } from "../theme/simplePanel";
import { defaultBtn, primaryBtn, withBtnStyle } from "../theme/styles";
import { NpcPortrait } from "./NpcPortrait";
import { useModalFocus } from "../logic/useModalFocus";
import { inertAttrs, inertOnClick } from "../logic/inert";

// Modal shown when a player picks an available challenge from the
// stepper. Shows the NPC, the challenge prep dialogue, and a deck
// picker (filtered by faction constraint when applicable). On
// confirm, calls `api.startChallenge` and hands the engine session
// id back to the parent (which navigates to /battle/<sid>).

interface Props {
  npcId: string;
  npc: api.NpcPayload;
  /** Which challenge set this entry belongs to. Threaded through to
   *  the start/skip endpoints — an NPC can host multiple sets, so
   *  npc_id alone isn't enough. */
  setId: string;
  entry: api.NpcChallengeEntry;
  onClose: () => void;
  onStarted: (sid: string) => void;
  /** Called after a successful admin-skip. The argument is the
   *  server-resolved `resume_node` — the dialogue node the parent
   *  should jump to (null = "fall through to the NPC's normal
   *  default-node resolution"). */
  onSkipped?: (resumeNodeId: string | null) => void;
}

export function ChallengeFightModal({
  npcId,
  npc,
  setId,
  entry,
  onClose,
  onStarted,
  onSkipped,
}: Props) {
  const theme = pixelTheme;
  const [decks, setDecks] = useState<api.DeckSummary[] | null>(null);
  const [selectedDeck, setSelectedDeck] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Set when a start is refused because the user already holds an
  // active match (commonly a match the boot-time resumer brought back
  // after a server restart). Carries that match's id so the modal can
  // offer to resume it or abandon it rather than dead-ending on
  // "End your current battle first."
  const [blockingMatchSid, setBlockingMatchSid] = useState<string | null>(null);
  const [sleep, setSleep] = useState<api.SleepState | null>(null);
  const [lowHpAck, setLowHpAck] = useState(false);
  const adminMode = useAdminMode();
  const dialogRef = useRef<HTMLDivElement>(null);
  useModalFocus(dialogRef, onClose);

  useEffect(() => {
    void (async () => {
      try {
        const { decks } = await api.listDecks();
        const filtered = filterDecksByConstraint(decks, entry.constraint);
        setDecks(filtered);
        setSelectedDeck(filtered[0]?.id ?? null);
      } catch (e) {
        setError(e instanceof Error ? e.message : "Failed to load decks.");
      }
    })();
  }, [entry]);

  useEffect(() => {
    void (async () => {
      try {
        const me = await api.getMe();
        setSleep(me.user.sleep);
      } catch {
        // HP warning is a UX nicety — if /me fails, fall back to letting
        // the player fight unwarned rather than blocking the modal.
      }
    })();
  }, []);

  const liveHp = sleep ? api.liveHp(sleep) : null;
  const maxHp = sleep?.max_hp ?? null;
  const needsLowHpWarning =
    liveHp != null && maxHp != null && liveHp < maxHp && !lowHpAck;

  // The actual start call, shared by FIGHT and by the "abandon &
  // start" recovery path. Throws on failure; callers translate.
  async function startChallengeNow() {
    const chosen = decks?.find((d) => d.id === selectedDeck);
    if (!chosen) return;
    const r = await api.startChallenge(
      npcId,
      setId,
      entry.index,
      api.toDeckRef(chosen),
    );
    onStarted(r.engine_session_id);
  }

  function errorCode(e: unknown): string | null {
    return e instanceof api.ApiError && e.body && typeof e.body === "object"
      ? ((e.body as { error?: string }).error ?? null)
      : null;
  }

  // Translate a start failure. `already_in_match` is special: rather
  // than show a dead-end message, we look up the blocking match so the
  // modal can offer Resume / Abandon. Returns true if it switched into
  // the blocking-match flow, false if it just set a plain error.
  async function handleStartError(e: unknown): Promise<void> {
    const code = errorCode(e);
    if (code === "already_in_match") {
      try {
        const { active } = await api.getActiveGame();
        if (active) {
          setBlockingMatchSid(active.id);
          setError(null);
          setBusy(false);
          return;
        }
      } catch {
        // Fall through to the plain message if we can't resolve it.
      }
    }
    setError(
      code
        ? friendlyChallengeError(code)
        : e instanceof Error
          ? e.message
          : "Failed to start challenge.",
    );
    setBusy(false);
  }

  async function onFight() {
    if (selectedDeck == null) return;
    if (needsLowHpWarning) {
      setLowHpAck(true);
      return;
    }
    setBusy(true);
    setError(null);
    setBlockingMatchSid(null);
    try {
      await startChallengeNow();
    } catch (e) {
      await handleStartError(e);
    }
  }

  // Jump into the match that's blocking this start. `onStarted`
  // navigates to /battle/<sid> — resuming an existing match is the
  // same navigation as entering a freshly-started one.
  function onResumeBlocking() {
    if (blockingMatchSid) onStarted(blockingMatchSid);
  }

  // Concede the blocking match, then start this challenge. Conceding
  // works even when the resumed match's engine session can't be
  // served (the server classifies it as abandoned and cleans up).
  async function onAbandonAndStart() {
    if (!blockingMatchSid) return;
    setBusy(true);
    setError(null);
    try {
      await api.endGame(blockingMatchSid);
      setBlockingMatchSid(null);
      await startChallengeNow();
    } catch (e) {
      await handleStartError(e);
    }
  }

  async function onSkip() {
    setBusy(true);
    setError(null);
    try {
      const r = await api.adminSkipChallenge(npcId, setId, entry.index);
      onSkipped?.(r.resume_node);
    } catch (e) {
      const msg =
        e instanceof api.ApiError && e.body && typeof e.body === "object"
          ? friendlyChallengeError((e.body as { error?: string }).error ?? null)
          : e instanceof Error
            ? e.message
            : "Skip failed.";
      setError(msg);
      setBusy(false);
    }
  }

  // Speaker for the modal portrait + name. Falls back to the page's
  // primary identity when the entry doesn't declare one — no impact
  // on Pip / Ned / Wolfgarde.
  const speaker: api.NpcSpeaker = entry.speaker ?? primarySpeaker(npc);

  const skipVisible = !!(adminMode && onSkipped);

  return (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-label={`Fight ${entry.title}`}
      tabIndex={-1}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(26, 15, 5, 0.55)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 100,
      }}
    >
      <div
        style={{
          ...simplePanelStyle(theme),
          padding: 24,
          minWidth: 480,
          maxWidth: 600,
          display: "flex",
          flexDirection: "column",
          gap: 16,
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "100px 1fr",
            gap: 14,
            alignItems: "center",
          }}
        >
          <NpcPortrait
            faction={speaker.faction}
            npcId={speaker.portrait_id}
            mood={speaker.default_mood}
            size={100}
          />
          <div>
            <div
              style={{
                fontFamily: theme.fontDisplay,
                fontSize: 14,
                letterSpacing: theme.letterSpacing,
                color: theme.ink,
              }}
            >
              {speaker.name.toUpperCase()}
            </div>
            <div
              style={{
                fontFamily: theme.fontDisplay,
                fontSize: 9,
                color: theme.inkFaint,
                letterSpacing: theme.letterSpacing,
                marginTop: 4,
              }}
            >
              CHALLENGE {entry.index + 1}
            </div>
          </div>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <div
            style={{
              fontFamily: theme.fontDisplay,
              fontSize: 11,
              letterSpacing: theme.letterSpacing,
              color: theme.inkFaint,
            }}
          >
            CHOOSE A DECK
            {entry.constraint && ` (${constraintLabel(entry.constraint)})`}
          </div>
          {decks === null ? (
            <div style={{ color: theme.inkFaint }}>Loading...</div>
          ) : decks.length === 0 ? (
            <div style={{ color: E32.scarlet, fontSize: 14 }}>
              {entry.constraint
                ? noEligibleDecksMessage(entry.constraint)
                : "You don't have any decks."}
            </div>
          ) : (
            <div style={{ display: "grid", gap: 4 }}>
              {decks.map((d) => (
                <label
                  key={d.id}
                  style={{
                    display: "flex",
                    gap: 10,
                    padding: "8px 10px",
                    border: `2px solid ${selectedDeck === d.id ? theme.accent : theme.panelEdge}`,
                    cursor: "pointer",
                  }}
                >
                  <input
                    type="radio"
                    name="deck"
                    checked={selectedDeck === d.id}
                    onChange={() => setSelectedDeck(d.id)}
                  />
                  <span>
                    {d.name}{" "}
                    <span style={{ color: theme.inkFaint, fontSize: 12 }}>
                      [{d.faction}]
                    </span>
                  </span>
                </label>
              ))}
            </div>
          )}
        </div>

        {needsLowHpWarning && (
          <div
            style={{
              padding: 10,
              border: `2px solid ${E32.amber}`,
              color: E32.amber,
              fontFamily: theme.fontDisplay,
              fontSize: 11,
              letterSpacing: theme.letterSpacing,
              lineHeight: 1.5,
            }}
          >
            WARNING: YOU ARE AT {liveHp}/{maxHp} HP. REST AT THE INN OR EAT
            FOOD TO HEAL FIRST, OR CLICK FIGHT AGAIN TO PROCEED ANYWAY.
          </div>
        )}

        <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
          <button type="button" onClick={onClose} {...defaultBtn(theme, busy)}>
            CANCEL
          </button>
          <button
            type="button"
            onClick={inertOnClick(busy || selectedDeck == null, () => void onFight())}
            {...inertAttrs(busy || selectedDeck == null)}
            {...primaryBtn(theme, busy || selectedDeck == null)}
          >
            {busy
              ? "STARTING…"
              : needsLowHpWarning
                ? "FIGHT ANYWAY"
                : "FIGHT"}
          </button>
        </div>

        {skipVisible && (
          // Admin-only escape hatch. Sits below the normal action
          // row so it's clearly separate from the regular flow and
          // a tester won't reach for it by reflex. Authentication +
          // Admin Mode toggle are double-checked server-side.
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              borderTop: `1px solid ${theme.panelEdge}`,
              paddingTop: 8,
            }}
          >
            <button
              type="button"
              onClick={inertOnClick(busy, () => void onSkip())}
              {...inertAttrs(busy)}
              {...withBtnStyle(defaultBtn(theme, busy), {
                background: E32.oxblood,
                color: E32.parchment,
                borderColor: E32.ruby,
                fontFamily: theme.fontDisplay,
                letterSpacing: theme.letterSpacing,
              })}
              title="Mark this challenge complete without battling. Admin testing only."
            >
              {busy ? "SKIPPING…" : "SKIP (ADMIN) ⏭"}
            </button>
          </div>
        )}

        {blockingMatchSid && (
          <div
            style={{
              padding: 10,
              border: `2px solid ${E32.amber}`,
              color: E32.amber,
              fontFamily: theme.fontDisplay,
              fontSize: 11,
              letterSpacing: theme.letterSpacing,
              lineHeight: 1.5,
              display: "flex",
              flexDirection: "column",
              gap: 10,
            }}
          >
            <div>
              YOU'RE ALREADY IN A BATTLE. RESUME IT, OR ABANDON IT TO START
              THIS CHALLENGE.
            </div>
            <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
              <button
                type="button"
                onClick={inertOnClick(busy, () => void onAbandonAndStart())}
                {...inertAttrs(busy)}
                {...withBtnStyle(defaultBtn(theme, busy), {
                  background: E32.oxblood,
                  color: E32.parchment,
                  borderColor: E32.ruby,
                  fontFamily: theme.fontDisplay,
                  letterSpacing: theme.letterSpacing,
                })}
              >
                {busy ? "WORKING…" : "ABANDON & START"}
              </button>
              <button
                type="button"
                onClick={inertOnClick(busy, onResumeBlocking)}
                {...inertAttrs(busy)}
                {...primaryBtn(theme, busy)}
              >
                RESUME BATTLE →
              </button>
            </div>
          </div>
        )}

        {error && (
          <div
            style={{
              padding: 10,
              border: `2px solid ${E32.scarlet}`,
              color: E32.scarlet,
              fontFamily: theme.fontDisplay,
              fontSize: 11,
              letterSpacing: theme.letterSpacing,
            }}
          >
            {error}
          </div>
        )}
      </div>
    </div>
  );
}

function friendlyChallengeError(code: string | null): string {
  if (!code) return "Challenge start failed.";
  switch (code) {
    case "previous_challenge_not_completed":
      return "Complete the earlier challenge first.";
    case "already_completed_today":
      return "You've already cleared that today.";
    case "already_in_match":
      return "End your current battle first.";
    case "deck_not_found":
      return "Couldn't find that deck.";
    case "guest_blocked":
      return "Guests can't take challenges. Convert your account first.";
  }
  const must = code.match(/^deck_must_be_(.+)$/);
  if (must) {
    const factions = humanizeFactions(must[1].split("_or_"));
    return `That challenge needs ${factions} deck.`;
  }
  const mustNot = code.match(/^deck_must_not_be_(.+)$/);
  if (mustNot) {
    const factions = humanizeFactions(mustNot[1].split("_or_"));
    return `That challenge needs a deck that isn't ${factions}.`;
  }
  return `Challenge start failed: ${code}`;
}

function filterDecksByConstraint(
  decks: api.DeckSummary[],
  constraint: api.NpcChallengeEntry["constraint"],
): api.DeckSummary[] {
  if (!constraint) return decks;
  if (constraint.kind === "faction") {
    return decks.filter((d) => constraint.factions.includes(d.faction));
  }
  return decks.filter((d) => !constraint.factions.includes(d.faction));
}

function constraintLabel(constraint: NonNullable<api.NpcChallengeEntry["constraint"]>): string {
  const list = constraint.factions.map((f) => f.toUpperCase()).join(" / ");
  return constraint.kind === "faction" ? `${list} ONLY` : `NON-${list}`;
}

function noEligibleDecksMessage(
  constraint: NonNullable<api.NpcChallengeEntry["constraint"]>,
): string {
  const factions = humanizeFactions(constraint.factions);
  return constraint.kind === "faction"
    ? `You don't have any ${factions} decks.`
    : `You don't have any non-${factions} decks.`;
}

function humanizeFactions(factions: string[]): string {
  if (factions.length === 0) return "";
  if (factions.length === 1) return factions[0];
  if (factions.length === 2) return `${factions[0]} or ${factions[1]}`;
  return `${factions.slice(0, -1).join(", ")}, or ${factions[factions.length - 1]}`;
}
